import type { LessonListItem } from '@/types/apis/Lesson';

import React from 'react';
import {
  MediaControlBar,
  MediaController,
  MediaFullscreenButton,
  MediaMuteButton,
  MediaPlayButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaTimeDisplay,
  MediaTimeRange,
} from 'media-chrome/react';

import { Button } from '../ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '../ui/dialog';

import LessonLikeDislike from './LessonLikeDislike';
import TagLesson from './TagLesson';

import ArrowLeftIcon from '@/svgs/arrowLeft.svg';
import BackwardIcon from '@/svgs/playerBackward.svg';
import ForwardIcon from '@/svgs/playerForward.svg';
import FullscreenIcon from '@/svgs/playerFullscreen.svg';
import PauseIcon from '@/svgs/playerPause.svg';
import PlayIcon from '@/svgs/playerPlay.svg';
import { fonts } from '@/theme/fonts';

type Props = {
  video_url: string;
  isOpen?: boolean;
  onCloseModal?: () => void;
  lesson?: LessonListItem;
};

const VideoModal: React.FC<Props> = ({
  video_url,
  isOpen = false,
  onCloseModal,
  lesson,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onCloseModal}>
      <DialogTitle className="hidden">Video</DialogTitle>
      <DialogContent
        className="w-dvw h-dvh max-w-dvw rounded-none border-none p-0 gap-0"
        dir="ltr"
        hideCloseButton
      >
        <DialogClose asChild>
          <Button
            type="button"
            variant="link"
            className="absolute top-4 right-4 z-10 p-1.5 w-8 h-8 rounded-[6px] opacity-50 bg-gray-950"
          >
            <ArrowLeftIcon className="size-5 rotate-180   [&>path]:stroke-white" />
          </Button>
        </DialogClose>
        <style>
          {`media-controller[mediaisfullscreen] {
              #lessonInfo {
                display: none
              }
            }
            media-time-range {
              --media-range-thumb-background: var(--color-primary);
              --media-range-bar-color: var(--color-primary);
              --media-range-track-background: var(--muted-foreground);
              --media-range-padding: 0px;
              --media-preview-time-margin: 0 0 -15px;
            }
            media-control-bar {
              --media-control-background: transparent;
              --media-control-hover-background: none;
            }`}
        </style>
        <MediaController className="h-full flex justify-center items-center bg-amber-50">
          <video
            slot="media"
            className="max-h-dvh"
            src={video_url}
            preload="auto"
            autoPlay
            loop
            crossOrigin=""
          />
          <MediaControlBar className="relative flex flex-col items-center justify-between w-full px-4 py-2 bg-gray-950  -mb-px">
            <MediaTimeRange className="w-full h-1 min-h-1  absolute -top-1" />
            <div className=" w-full h-[70px] flex flex-row-reverse justify-between gap-2">
              <div className="flex flex-row-reverse items-center justify-start gap-3 flex-1 grow pt-2">
                <MediaFullscreenButton noTooltip>
                  <span className="size-6" slot="icon">
                    <FullscreenIcon />
                  </span>
                </MediaFullscreenButton>
                <MediaMuteButton noTooltip />
              </div>
              <div className="flex flex-row-reverse items-center justify-around gap-7 flex-1 grow">
                <MediaSeekForwardButton noTooltip>
                  <div className="size-6" slot="icon">
                    <ForwardIcon />
                  </div>
                </MediaSeekForwardButton>
                <MediaPlayButton noTooltip>
                  <div className="size-[52px]" slot="pause">
                    <PauseIcon />
                  </div>
                  <div className="size-[52px]" slot="play">
                    <PlayIcon />
                  </div>
                </MediaPlayButton>
                <MediaSeekBackwardButton noTooltip>
                  <div className="size-6" slot="icon">
                    <BackwardIcon />
                  </div>
                </MediaSeekBackwardButton>
              </div>
              <div className="flex flex-row-reverse items-center justify-end gap-3 flex-1 grow pt-2">
                <MediaTimeDisplay
                  showDuration
                  className={`${fonts.className}`}
                />
              </div>
            </div>
            {lesson && (
              <div
                id="lessonInfo"
                dir="rtl"
                className="flex h-20 bg-gray-950 pt-6 pb-4 w-full gap-2"
              >
                <div className="flex flex-1 flex-col max-w-[50%]">
                  <p className="font-bold text-xs truncate">{lesson.title}</p>
                  <p className="font-normal text-xs truncate">
                    {lesson.description}
                  </p>
                </div>
                <div className="flex flex-1  gap-4 items-center justify-end">
                  <TagLesson title={lesson.content_category.title} />
                  <LessonLikeDislike lesson={lesson} />
                </div>
              </div>
            )}
          </MediaControlBar>
        </MediaController>
      </DialogContent>
    </Dialog>
  );
};

export default VideoModal;
